import { Link } from 'react-router-dom';
import type { LessonLog, TestRecord } from '@/types';
import { formatDate } from '@/lib/datetime';

export interface RecentLesson extends LessonLog {
  studentName: string;
  tests: TestRecord[];
}

interface RecentLessonsCardProps {
  lessons: RecentLesson[];
}

export default function RecentLessonsCard({ lessons }: RecentLessonsCardProps) {
  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-book-2-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">최근 수업일지</h2>
        </div>
        <Link
          to="/lessons"
          className="flex items-center gap-0.5 text-xs font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap"
        >
          전체 보기
          <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      {lessons.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-12">
          <i className="ri-book-open-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">아직 기록된 수업일지가 없어요.</p>
          <Link
            to="/lessons/new"
            className="mt-1 flex items-center gap-1 rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-edit-2-line"></i>
            수업일지 작성하기
          </Link>
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {lessons.map((l) => {
            const d = new Date(l.lesson_date);
            const valid = !Number.isNaN(d.getTime());
            return (
              <Link
                key={l.id}
                to={`/lessons/${l.id}/edit`}
                className="flex items-start gap-3.5 rounded-lg border border-background-200 bg-background-100 p-3.5 transition hover:border-primary-300"
              >
                <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <span className="text-[11px] font-medium">{valid ? `${d.getMonth() + 1}월` : '-'}</span>
                  <span className="text-base font-bold leading-none">{valid ? d.getDate() : '-'}</span>
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-foreground-950">{l.studentName}</span>
                    {l.is_absent ? (
                      <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">
                        결석
                      </span>
                    ) : (
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-900">
                        출석
                      </span>
                    )}
                    <span className="text-[11px] text-foreground-500">{formatDate(l.lesson_date)}</span>
                  </div>
                  <p className="mt-1 truncate text-sm text-foreground-700">
                    {l.lesson_scope || '수업 범위 미입력'}
                  </p>
                  {l.next_homework && (
                    <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-foreground-500">
                      <i className="ri-edit-box-line"></i>
                      {l.next_homework}
                    </p>
                  )}
                  {l.tests.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {l.tests.map((t) => (
                        <span
                          key={t.id}
                          className="flex items-center gap-1 rounded-full bg-accent-50 px-2 py-0.5 text-[11px] font-medium text-accent-800"
                        >
                          <i className="ri-quill-pen-line"></i>
                          {t.test_scope || '테스트'}
                          {t.score && <span className="font-bold">· {t.score}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <i className="ri-arrow-right-s-line mt-1 shrink-0 text-foreground-400"></i>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}